import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  BookOpenIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  ShoppingBagIcon,
  CalendarIcon,
  EnvelopeIcon,
  HeartIcon,
  MapPinIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import iconLogo from '../assets/icon.png';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const PublicFooter: React.FC = () => {
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement newsletter signup
    console.log('Newsletter signup:', email);
    setEmail('');
  };

  return (
    <footer className="hidden md:block bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mr-3 bg-gradient-to-br from-primary-500 to-secondary-500">
              <img src={iconLogo} alt="ProfStudy Logo" className="w-8 h-8 rounded-lg" />
            </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                ProfStudy
              </span>
            </div>
            <p className="text-gray-300 text-base leading-relaxed mb-6 max-w-md">
              Your gateway to professional excellence in ICAG & CITG. Empowering students with quality education, 
              comprehensive study materials, and expert guidance for career success.
            </p>
            
            {/* Social Media Links */}
            <div className="flex space-x-4">
              <a href="https://facebook.com/ProfsTraining" aria-label="Facebook" title="Facebook" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-200 group">
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white" role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2.04c-5.5 0-9.96 4.46-9.96 9.96 0 4.99 3.66 9.13 8.44 9.88v-6.99H7.9v-2.89h2.58V8.41c0-2.55 1.5-3.96 3.8-3.96 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.89h-2.34v6.99c4.78-.75 8.44-4.89 8.44-9.88 0-5.5-4.46-9.96-9.96-9.96z"/></svg>
              </a>
              <a href="https://linkedin.com/in/profs-training-solutions/" aria-label="LinkedIn" title="LinkedIn" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-200 group">
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white" role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="http://instagram.com/profs_training_solutions/" aria-label="Instagram" title="Instagram" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-200 group">
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white" role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.42.403a4.9 4.9 0 011.77 1.153 4.9 4.9 0 011.153 1.77c.163.45.35 1.25.403 2.42.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.403 2.42a4.9 4.9 0 01-1.153 1.77 4.9 4.9 0 01-1.77 1.153c-.45.163-1.25.35-2.42.403-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.42-.403a4.9 4.9 0 01-1.77-1.153 4.9 4.9 0 01-1.153-1.77c-.163-.45-.35-1.25-.403-2.42C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.403-2.42a4.9 4.9 0 011.153-1.77 4.9 4.9 0 011.77-1.153c.45-.163 1.25-.35 2.42-.403C8.416 2.175 8.796 2.163 12 2.163zm0 3.674a6.163 6.163 0 100 12.326 6.163 6.163 0 000-12.326zm0 1.837a4.326 4.326 0 110 8.651 4.326 4.326 0 010-8.651zm6.406-.919a1.44 1.44 0 110 2.88 1.44 1.44 0 010-2.88z"/></svg>
              </a>
              <a href="http://youtube.com/@ProfsTrainingSolutions" aria-label="YouTube" title="YouTube" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-200 group">
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white" role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M23.498 6.186a2.97 2.97 0 00-2.088-2.103C19.62 3.5 12 3.5 12 3.5s-7.62 0-9.41.583A2.97 2.97 0 00.502 6.186C0 7.98 0 12 0 12s0 4.02.502 5.814a2.97 2.97 0 002.088 2.103C4.38 20.5 12 20.5 12 20.5s7.62 0 9.41-.583a2.97 2.97 0 002.088-2.103C24 16.02 24 12 24 12s0-4.02-.502-5.814zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>
              </a>
              <a href="https://whatsapp.com/channel/0029VaZV6OeEAKWHHdSdiR1S" aria-label="WhatsApp Channel" title="WhatsApp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-200 group">
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white" role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.62-6.003C.122 5.281 5.403 0 12.057 0c3.181 0 6.167 1.24 8.413 3.488a11.82 11.82 0 013.49 8.41c-.003 6.654-5.284 11.935-11.94 11.935a11.9 11.9 0 01-6.003-1.617L.057 24zm6.597-3.807c1.735 1.034 3.276 1.666 5.392 1.666 5.448 0 9.886-4.434 9.889-9.877.003-5.463-4.415-9.89-9.881-9.894-5.451 0-9.886 4.434-9.889 9.881a9.82 9.82 0 001.662 5.42l-.999 3.648 3.826-.844zm11.387-5.464c-.074-.124-.272-.198-.568-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.667.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.667-1.611-.914-2.206-.24-.579-.485-.5-.667-.51l-.568-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.718 2.006-1.412.248-.694.248-1.29.173-1.412z"/></svg>
              </a>
            </div>
          </div>
          
          {/* Programs Section */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white border-b border-gray-600 pb-2">
              Programs
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/catalog?program=ICAG" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <AcademicCapIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  ICAG Courses
                </Link>
              </li>
              <li>
                <Link to="/catalog?program=CITG" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <AcademicCapIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  CITG Courses
                </Link>
              </li>
              <li>
                <Link to="/free-resources" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <BookOpenIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Study Materials
                </Link>
              </li>
              <li>
                <Link to="/classrooms" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <VideoCameraIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Video Lectures
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Explore Section (only pages that exist) */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white border-b border-gray-600 pb-2">
              Explore
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/blog" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <DocumentTextIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <HeartIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Testimonials
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <PhotoIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Gallery
                </Link>
              </li>
              {!shouldShowIOSWebViewAlert() && (
                <li>
                  <Link to="/shop" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                    <ShoppingBagIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                    Shop
                  </Link>
                </li>
              )}
              <li>
                <Link to="/study-forums" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Study Forums
                </Link>
              </li>
              <li>
                <Link to="/live-meetings" className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group">
                  <CalendarIcon className="h-4 w-4 mr-3 text-primary-500 group-hover:scale-110 transition-transform duration-200" />
                  Live Meetings
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter Section */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold mb-6 text-white border-b border-gray-600 pb-2 flex items-center">
              <EnvelopeIcon className="h-5 w-5 mr-2 text-primary-400" />
              Stay Updated
            </h3>
            <p className="text-gray-300 mb-4">
              Get the latest updates on new courses, study materials, and exclusive offers.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  required
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 hover:scale-105"
                >
                  Subscribe
                </button>
              </div>
              <p className="text-xs text-gray-400">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          </div>
          </div>
        </div>
        
      {/* Bottom Footer Bar */}
      <div className="border-t border-gray-700 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6 text-sm text-gray-400">
          <p>&copy; 2024 ProfStudy. All rights reserved.</p>
              <span className="hidden md:inline">•</span>
              <Link to="/terms-and-conditions" className="hover:text-white transition-colors duration-200">
                Terms & Conditions
              </Link>
              <span className="hidden md:inline">•</span>
              <Link to="/legal" className="hover:text-white transition-colors duration-200">
                Privacy Policy
              </Link>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <span>Made with ❤️ in Ghana</span>
              <span className="hidden md:inline">•</span>
              <a href="https://wa.me/233242723139" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">
                Built by Redizeuz Tech
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
